import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
} from 'react';
import { useTranslation } from 'next-i18next';
import type { Swiper as SwiperType } from 'swiper';
import Heading from '@components/ui/Heading';
import useObjectVisibility from '@hooks/useObjectVisibility';
import {
  AlgonomyProduct,
  AlgonomyPlacement,
} from '@components/global/Recommendations/Loader';
import {
  GA4_EVENT_NAMES,
  trackProductEvent,
  generateProductTrackingData,
} from '@utils/gtm/ga4';
import { setSelectItemCookie } from '@utils/gtm';
import { getVisibleSlideIndexes } from '@utils/swiper';
import Carousel, { useCarousel } from '_components/Carousel';
import MiniProductCard from '_components/MiniProductStrip/MiniProductCard';
import styles from './MiniProductStrip.module.scss';

export interface ProductStripProps {
  placement: AlgonomyPlacement;
  headingLevel?: 'h2' | 'h3';
}

const { SELECT_ITEM, VIEW_ITEM_LIST } = GA4_EVENT_NAMES;

// ─── Inner component (has access to Carousel context) ─────────────────────────

interface MiniProductStripInner {
  placement: AlgonomyPlacement;
  headingLevel: 'h2' | 'h3';
  headingId: string;
  isVisible: boolean;
}

function MiniProductStripInner({
  placement,
  headingLevel,
  headingId,
  isVisible,
}: MiniProductStripInner) {
  const { t } = useTranslation('common');
  const {
    meta: { swiper, updateNavVisibility },
  } = useCarousel();

  const viewedSlidesRef = useRef<number[]>([]);
  const [hasInitialTrackingFired, setInitialTrackingFired] = useState(false);

  const preloadAhead = useCallback(
    (activeIndex: number, count = 2) => {
      for (let i = activeIndex + 1; i <= activeIndex + count; i++) {
        const url = placement.items[i]?.imageURL;
        if (url) {
          const img = new window.Image();
          img.src = url;
        }
      }
    },
    [placement.items]
  );

  const generateTrackingData = useCallback(
    (i: number) => {
      if (!placement?.items?.[i]) return null;
      return generateProductTrackingData(
        placement.items[i],
        i,
        placement?.name
      );
    },
    [placement]
  );

  const trackClick = useCallback(
    (i: number) => {
      const product = generateTrackingData(i);
      if (product) {
        setSelectItemCookie({
          eventName: SELECT_ITEM,
          products: [product],
          itemListName: placement?.name ?? '',
        });
      }
    },
    [generateTrackingData, placement?.name]
  );

  // Stable per-card click handlers so memoized MiniProductCards don't re-render
  // on every nav-visibility change (which re-renders this context consumer).
  const itemClickHandlers = useMemo(
    () => placement.items.map((_, i) => () => trackClick(i)),
    [placement.items, trackClick]
  );

  const trackSlideView = useCallback(
    (visibleSlideIndexes: number[] = []) => {
      const untracked = visibleSlideIndexes.filter(
        (i) => !viewedSlidesRef.current.includes(i)
      );
      if (!untracked.length) return;
      trackProductEvent({
        eventName: VIEW_ITEM_LIST,
        products: untracked.map((i) => generateTrackingData(i)),
        itemListName: placement?.name,
      });
      viewedSlidesRef.current = [...viewedSlidesRef.current, ...untracked];
    },
    [generateTrackingData, placement?.name]
  );

  // Fire initial tracking when the strip enters the viewport
  useEffect(() => {
    if (swiper && isVisible && !hasInitialTrackingFired) {
      trackSlideView(getVisibleSlideIndexes(swiper));
      preloadAhead(swiper.activeIndex);
      setInitialTrackingFired(true);
    }
  }, [
    isVisible,
    swiper,
    trackSlideView,
    hasInitialTrackingFired,
    preloadAhead,
  ]);

  // Re-evaluate nav state when Algonomy items arrive after swiper initialises
  useEffect(() => {
    if (placement?.items?.length && swiper) {
      updateNavVisibility(swiper);
    }
  }, [placement?.items?.length, swiper, updateNavVisibility]);

  return (
    <>
      <div className={styles.header}>
        <Heading
          semanticLevel={headingLevel}
          styleLevel="2"
          className={styles.heading}
          id={headingId}
        >
          {placement?.message}
        </Heading>
        <div className={styles.buttonGroup}>
          <Carousel.NavPrev
            className={styles.navButton}
            aria-label={t('productStrip.navPrev')}
          />
          <Carousel.NavNext
            className={styles.navButton}
            aria-label={t('productStrip.navNext')}
          />
        </div>
      </div>

      <Carousel.Track
        className={styles.swiper}
        autoHeight
        spaceBetween={8}
        onSlideChange={(s: SwiperType) => {
          trackSlideView(getVisibleSlideIndexes(s));
          preloadAhead(s.activeIndex);
        }}
      >
        {placement.items.map((product: AlgonomyProduct, i: number) => (
          <Carousel.Slide key={product.id} className={styles.slide}>
            <MiniProductCard
              displayName={product.displayName}
              displayPrice={product.displayPrice}
              imageURL={product.imageURL}
              href={product?.linkURL ?? product?.detailURL}
              displaySalePrice={product.displaySalePrice}
              withBorder={true}
              trackClick={itemClickHandlers[i]}
            />
          </Carousel.Slide>
        ))}
      </Carousel.Track>
    </>
  );
}

// ─── Public component ─────────────────────────────────────────────────────────

const MiniProductStrip = ({
  placement,
  headingLevel = 'h2',
}: ProductStripProps) => {
  const headingId = useId();
  const containerRef = useRef<HTMLElement | null>(null);
  const isCarouselVisible = useObjectVisibility(containerRef);

  if (!placement?.items?.length) return null;

  return (
    <section
      className={styles.root}
      aria-labelledby={headingId}
      ref={containerRef}
    >
      <Carousel.Provider>
        <MiniProductStripInner
          placement={placement}
          headingLevel={headingLevel}
          headingId={headingId}
          isVisible={isCarouselVisible}
        />
      </Carousel.Provider>
    </section>
  );
};

export default MiniProductStrip;
